import { useCallback, useContext, useMemo } from 'react'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import LinePenIcon from 'LuxKit/Icons/line/LinePenIcon'
import Image from 'components/Common/Image'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import TileCancellationPolicy from 'components/SearchV2/SearchTileCancellationPolicy/TileCancellationPolicy'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import GeoContext from 'contexts/geoContext'
import { isHotelPackage } from 'lib/offer/offerTypes'
import CartPanelAccommodationTileLayout from './CartPanelAccommodationTileLayout'
import CartPanelPickedForYouPill from './CartPanelPickedForYouPill'
import useCartPanelHotelOfferData, { type CartPanelHotelOffer } from '../hooks/useCartPanelHotelOfferData'
import type { CartPanelAccommodationItem } from '../hooks/useCartPanelItems'
import { isCartPanelAccommodationUnavailableStatus } from '../utils/cartPanelUtils'
import { useTripPlannerEventBus } from '../contexts/TripPlannerEventBus'

const RoomCard = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.M};
  padding: ${rem(8)} ${rem(16)} ${rem(8)} ${rem(8)};
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  width: 100%;
  max-width: ${rem(500)};
  container-type: inline-size;
  container-name: room-card;
`

const RoomCardRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(12)};
`

const RoomCardRightColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  flex-wrap: wrap;
`

const ChangeRoomButtonWrapper = styled.div`
  @container room-card (max-width: ${rem(320)}) {
    flex-basis: 100%;
    display: flex;
    justify-content: flex-start;
  }
`

const RoomImageWrapper = styled.div`
  width: ${rem(68)};
  height: ${rem(68)};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
  flex-shrink: 0;
`

const RoomCardContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
`

const RoomName = styled(BodyText)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const CheckoutDetailsBanner = styled(MessageBanner)`
  > div {
    padding-top: ${rem(8)};
    padding-bottom: ${rem(8)};
  }

  a,
  button {
    --color-primary-bg: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
    --color-primary-hover: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
    --color-primary-active: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
    --button-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
    --button-border-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
    --button-hover-color: ${props => props.theme.palette?.neutral?.surface?.darkContrast ?? '#FFFFFF'};
    --button-hover-background-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
    --button-hover-border-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
    --button-active-color: ${props => props.theme.palette?.neutral?.surface?.darkContrast ?? '#FFFFFF'};
    --button-active-background-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
    --button-active-border-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
    --button-focus-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
    --button-focus-border-color: ${props => props.theme.palette?.neutral?.default?.one ?? '#363A45'};
  }
`

function getRoomImage(pkg: App.Package | App.BedbankPackage | undefined): App.Image | undefined {
  if (!pkg) return undefined
  if ('roomType' in pkg) return pkg.roomType?.images?.[0]
  if ('images' in pkg) return pkg.images[0]
  return undefined
}

function getCancellationPolicyType(pkg: App.Package | App.BedbankPackage | undefined) {
  if (!pkg || !('roomRate' in pkg)) return undefined
  return pkg.roomRate?.cancellationPolicy.type
}

function isHotelOffer(offer: CartPanelHotelOffer | undefined): offer is App.HotelOffer {
  return !!offer && offer.type !== 'bedbank_hotel'
}

interface Props {
  item: CartPanelAccommodationItem
  onToggleInclude: (item: CartPanelAccommodationItem) => void
  onDelete: (item: CartPanelAccommodationItem) => void
  isStreaming: boolean
}

function CartPanelActiveHotelTile({
  item,
  onToggleInclude,
  onDelete,
  isStreaming,
}: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const { offer, pkg, checkIn: cartCheckIn, checkOut: cartCheckOut } = useCartPanelHotelOfferData(item)
  const eventBus = useTripPlannerEventBus()
  const checkIn = cartCheckIn ?? item.checkIn
  const checkOut = cartCheckOut ?? item.checkOut

  const handleCardClick = useCallback(() => {
    if (!item.offerId) return
    const searchParams = checkIn || checkOut ? { checkIn, checkOut } : undefined
    eventBus.dispatch({ type: 'open_offer_drawer', offerId: item.offerId, searchParams, scrollToRoomOptions: true })
  }, [item.offerId, checkIn, checkOut, eventBus])

  const handleDelete = useCallback(() => {
    onDelete(item)
  }, [onDelete, item])

  const isSoldOut = item.pricing.isAvailable === false
  const canChangeRoom = item.cartStatus !== 'property_sold_out'
  const shouldShowPickedForYou = item.packageAutoSelected && !isSoldOut && !isCartPanelAccommodationUnavailableStatus(item.cartStatus)

  const datesLabel = item.dates && item.duration
    ? intl.formatMessage(
        {
          id: 'VElcmYw8-6lH-Mgm2GV6',
          defaultMessage: '{dateRange} · {nights, plural, one {# night} other {# nights}}',
          description: 'Date range with duration in nights for a hotel stay tile in the trip planner cart panel',
        },
        { dateRange: item.dates, nights: item.duration },
      )
    : item.dates
  const durationLabel = !item.dates && item.duration
    ? intl.formatMessage(
        {
          id: 'GQY0DKErdsrlLFeRV9Ax',
          defaultMessage: '{nights}-night stay',
          description: 'Night count shown under a hotel name when dates are missing but the trip duration is known',
        },
        { nights: item.duration },
      )
    : undefined

  const loyaltyCalculationRequest = useMemo(() => {
    if (!isHotelOffer(offer) || !item.pricing.price) return undefined
    return generateLoyaltyPointsCalculatorHotelOptions(offer, {
      pkg: isHotelPackage(pkg) ? pkg : undefined,
      price: item.pricing.price,
      memberPrice: item.pricing.memberPrice,
      currentRegionCode,
      nights: item.duration,
    })
  }, [offer, pkg, item.pricing.price, item.pricing.memberPrice, item.duration, currentRegionCode])

  const roomImage = getRoomImage(pkg) ?? (item.roomImageId ? { id: item.roomImageId } : undefined)
  const cancellationPolicyType = getCancellationPolicyType(pkg)
  const shouldShowCheckoutDetailsBanner = !item.includeInTotal && !isSoldOut
  const checkoutDetailsBannerAction = item.dates ? intl.formatMessage({
    id: '9Tle--nXWQetSKbmQ8kA',
    defaultMessage: 'Choose package',
    description: 'Action button that opens the hotel drawer so the user can choose a package for checkout',
  }) : intl.formatMessage({
    id: '5NZpwNagm7j-mUPX1BLo',
    defaultMessage: 'Add dates',
    description: 'Action button that opens the hotel drawer so the user can add dates to the hotel',
  })
  const checkoutDetailsBannerDescription = item.dates ? intl.formatMessage({
    id: '9dBPdSSF02mnTeIDmw55',
    defaultMessage: 'Choose a package to include in checkout',
    description: 'Warning banner shown when hotel dates are selected but a package is needed for checkout',
  }) : intl.formatMessage({
    id: 'l37kymVhZxcxOCLV15q6',
    defaultMessage: 'Add dates to include in checkout',
    description: 'Warning banner prompting the user to add dates so the hotel can be included in checkout',
  })

  const checkoutDetailsBanner = shouldShowCheckoutDetailsBanner ? (
    <CheckoutDetailsBanner
      kind="warning"
      density="compact"
      primaryActionButtonProps={{
        children: checkoutDetailsBannerAction,
        onClick: handleCardClick,
        disabled: isStreaming || !item.offerId,
        size: 'small',
      }}
    >
      {checkoutDetailsBannerDescription}
    </CheckoutDetailsBanner>
  ) : undefined

  const header = (
    <Group direction="vertical" gap={4}>
      <Heading variant="heading4">{item.name}</Heading>
      {datesLabel && (
        <BodyText variant="medium" colour="neutral-three">
          {datesLabel}
        </BodyText>
      )}
      {durationLabel && (
        <BodyText variant="medium" colour="neutral-three">
          {durationLabel}
        </BodyText>
      )}
    </Group>
  )

  const roomCard = (item.roomName || item.occupancy) ? (
    <RoomCard>
      <RoomCardRow>
        {roomImage && (
          <RoomImageWrapper>
            <Image image={roomImage} width={80} height={80} fit="center" objectFit="cover" dpr={2} />
          </RoomImageWrapper>
        )}
        <RoomCardRightColumn>
          <RoomCardContent>
            {shouldShowPickedForYou && <CartPanelPickedForYouPill />}
            {item.roomName && <RoomName variant="medium" weight="bold">{item.roomName}</RoomName>}
            {item.occupancy && <BodyText variant="medium" colour="neutral-three">{item.occupancy}</BodyText>}
          </RoomCardContent>
          {canChangeRoom && (
            <ChangeRoomButtonWrapper>
              <TextLink size="caption" startIcon={<LinePenIcon />} onClick={handleCardClick} disabled={isStreaming || !item.offerId}>
                {intl.formatMessage({
                  id: 'BrfTYdJonj0A3hETLQR8',
                  defaultMessage: 'Change room',
                  description: 'Link to change the selected room for a hotel in the cart panel',
                })}
              </TextLink>
            </ChangeRoomButtonWrapper>
          )}
        </RoomCardRightColumn>
      </RoomCardRow>
    </RoomCard>
  ) : undefined

  const unavailableMessage = useMemo(() => {
    if (item.cartStatus === 'selected_rate_unavailable') {
      return intl.formatMessage({
        id: '2kkjaqCrQuCva2U9d_EV',
        defaultMessage: 'Selected room is no longer available for these dates.',
        description: 'Warning banner shown when a selected hotel room rate is no longer available in the trip planner cart panel',
      })
    }
    if (item.cartStatus === 'property_sold_out') {
      return intl.formatMessage({
        id: '8AilLDtim57tisL4rZPZ',
        defaultMessage: 'This hotel is sold out for these dates.',
        description: 'Warning banner shown when a hotel property is sold out for selected dates in the trip planner cart panel',
      })
    }
    if (isSoldOut) {
      return intl.formatMessage({
        id: '33gzZELMLS52IVDsupWp',
        defaultMessage: 'Sold out for your dates',
        description: 'Warning banner shown when a hotel in the cart panel is not available for the selected dates',
      })
    }
    return undefined
  }, [intl, isSoldOut, item.cartStatus])

  const attention = unavailableMessage ? (
    <MessageBanner kind="warning" density="compact" icon={<LineInfoCircleIcon />}>
      {unavailableMessage}
    </MessageBanner>
  ) : (
    <Group direction="vertical" gap={4}>
      {isHotelOffer(offer) && cancellationPolicyType && (
        <TileCancellationPolicy
          cancellationPolicyType={cancellationPolicyType}
          checkInDate={checkIn ?? ''}
          timezoneOffset={offer.property.timezoneOffset || 0}
          offerType={offer.type}
          textSize="medium"
        />
      )}
      {loyaltyCalculationRequest && (
        <LoyaltyPointsEarnWithBreakdownModal
          calculationRequests={[loyaltyCalculationRequest]}
          calculationType="exact"
        />
      )}
    </Group>
  )

  const deleteAction = (
    <TextButton kind="secondary" size="small" shape="round" startIcon={<LineTrashAltIcon />} onClick={handleDelete} disabled={isStreaming}>
      {intl.formatMessage({
        id: 'jDEZjhV3eo-cnxIBHOtR',
        defaultMessage: 'Delete',
        description: 'Button to permanently delete an item from the trip planner cart panel',
      })}
    </TextButton>
  )

  const price = item.pricing.price && !isSoldOut ? (
    <CartReviewPriceStack
      price={item.pricing.price}
      memberPrice={item.pricing.memberPrice}
      value={item.pricing.value ?? 0}
    />
  ) : undefined

  return (
    <CartPanelAccommodationTileLayout
      item={item}
      onToggleInclude={onToggleInclude}
      onCardClick={item.offerId ? handleCardClick : undefined}
      topBanner={checkoutDetailsBanner}
      header={header}
      roomCard={roomCard}
      attention={attention}
      deleteAction={deleteAction}
      price={price}
    />
  )
}

export default CartPanelActiveHotelTile
